import {inject, Injectable} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';

/**
 * Owns the `?cache=false` recovery contract (Bug #417).
 *
 * The contract has two ends and they must stay in step:
 *
 *  * `src/index.html`'s inline boot-recovery script produces the URL — automatically when a bundle 404s
 *    because the browser served a stale `index.html`, and manually behind the "Aktualisieren" link. It always
 *    emits `cache=false` **together with** `time=<epoch ms>`; the `time` part is what makes the URL a cache
 *    miss and therefore what actually fetches a fresh document.
 *  * This service is the other end: it purges whatever browser-side caches exist and then removes `cache`
 *    from the URL (keeping `time`, so the address stays the cache-busted one) via a history *replacement*, so
 *    the parameter cannot be re-triggered by a back navigation.
 *
 * There is deliberately **no unconditional page reload** here. The document that runs this code was itself
 * fetched from the cache-busting URL, so it is already fresh — reloading it again would turn every recovery
 * into two full page loads for no gain. A reload only happens when the purge actually removed something, i.e.
 * when a service worker or a Cache Storage entry existed and could have served this document's resources.
 *
 * That purge is defensive: this app registers no service worker (there is no `@angular/service-worker`
 * dependency and no `register()` call anywhere). It is kept because it is cheap and guards against a legacy
 * registration lingering in a long-lived browser profile — but nothing may be built on the assumption that
 * one exists.
 */
@Injectable({
  providedIn: 'root'
})
export class CachingService {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  /** Guards against handling the same request twice if `queryParams` re-emits before the URL is rewritten. */
  private handling = false;

  constructor() {
    this.register();
  }

  private register(): void {
    this.route.queryParams.subscribe(params => {
      if (params['cache'] !== 'false' || this.handling) return;
      this.handling = true;
      void this.purge();
    });
  }

  /** Purge, then strip `cache` from the URL; reload only if the purge could have affected this document. */
  private async purge(): Promise<void> {
    const purged = await this.reset();

    await this.router.navigate([], {
      queryParams: {cache: null},
      queryParamsHandling: 'merge',
      // Replace rather than push: a back navigation onto `?cache=false` would run the whole dance again.
      replaceUrl: true
    });

    // Only meaningful when something was actually removed — see the class comment on why this is conditional.
    if (purged) this.reload();
  }

  /**
   * Unregister every service worker and delete every Cache Storage entry.
   *
   * @returns `true` when at least one registration or cache was actually removed.
   */
  private async reset(): Promise<boolean> {
    let purged = false;

    // Feature-detect by value, not with `in`: `caches` is undefined outside a secure context, and both are
    // absent in a plain unit-test host — a missing API must degrade to "nothing to purge", never throw.
    const worker = (navigator as Navigator & {serviceWorker?: ServiceWorkerContainer}).serviceWorker;
    if (worker) {
      const registrations = await worker.getRegistrations();
      for (const registration of registrations) {
        purged = (await registration.unregister()) || purged;
      }
    }

    const storage = (window as Window & {caches?: CacheStorage}).caches;
    if (storage) {
      const cacheKeys = await storage.keys();
      for (const cacheKey of cacheKeys) {
        purged = (await storage.delete(cacheKey)) || purged;
      }
    }

    return purged;
  }

  /**
   * Full page reload.
   *
   * Its own method purely as a test seam: `window.location.reload` cannot be stubbed (the property is
   * non-configurable), so a spec that actually reloaded would tear the Karma page down mid-run.
   */
  private reload(): void {
    window.location.reload();
  }
}
